import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { errorFromResponse } from '@/lib/errors';
import { idempotencyKeyFor } from '@/lib/http-core';

/**
 * QA-457 — 운영자 주문 상태 변경(주문 단위, 고도몰 용어). order-service #71
 *   POST /api/admin/v1/orders/status-change { orderIds(1~100), targetStatus, courierCode?, trackingNo? }
 * 주문마다 성공·실패를 따로 돌려준다(일부 실패해도 200). 구매확정은 OPS_ADMIN·SUPER_ADMIN 만.
 */
export type OrderStatusTarget =
  | 'AWAITING_DEPOSIT'
  | 'PAID'
  | 'PREPARING'
  | 'SHIPPING'
  | 'DELIVERED'
  | 'PURCHASE_CONFIRMED'
  // QA-529 PG(KCP 등)에서 수기로 환불한 주문을 주문 상태도 환불완료로 맞춘다 — PG 환불은 하지 않는다(BE 전달 문서).
  | 'REFUND_DONE'
  // 입금대기(무통장·가상계좌) 주문 취소 — 가상계좌는 발급 계좌도 닫힌다. OPS·CS·SUPER 만(그 외 403).
  | 'CANCELED';

export const ORDER_STATUS_TARGETS: ReadonlyArray<OrderStatusTarget> = [
  'AWAITING_DEPOSIT',
  'PAID',
  'PREPARING',
  'SHIPPING',
  'DELIVERED',
  'PURCHASE_CONFIRMED',
  'REFUND_DONE',
  'CANCELED',
];

export const ORDER_STATUS_TARGET_LABELS: Record<OrderStatusTarget, string> = {
  AWAITING_DEPOSIT: '입금대기',
  PAID: '결제완료',
  PREPARING: '상품준비중',
  SHIPPING: '배송중',
  DELIVERED: '배송완료',
  PURCHASE_CONFIRMED: '구매확정',
  REFUND_DONE: '환불완료',
  CANCELED: '취소',
};

export interface OrderStatusChangeInput {
  orderIds: ReadonlyArray<string>;
  targetStatus: OrderStatusTarget;
  courierCode?: string;
  trackingNo?: string;
}

export interface OrderStatusChangeResult {
  orderId: string;
  orderNo: string;
  success: boolean;
  message: string | null;
  displayLabelCode: string | null;
}

export interface OrderStatusChangeResponse {
  successCount: number;
  failCount: number;
  results: OrderStatusChangeResult[];
}

const PATH = '/api/admin/v1/orders/status-change';

/** BE 한 번에 받는 최대 주문 수 — 넘으면 이만큼씩 나눠 차례로 보내고 결과를 합친다(QA-537 500개 보기). */
export const ORDER_STATUS_CHANGE_CHUNK = 100;

export const useOrderStatusChangeMutation = () => {
  const qc = useQueryClient();
  return useMutation<OrderStatusChangeResponse, Error, OrderStatusChangeInput>({
    mutationFn: async (input) => {
      const merged: OrderStatusChangeResponse = { successCount: 0, failCount: 0, results: [] };
      for (let i = 0; i < input.orderIds.length; i += ORDER_STATUS_CHANGE_CHUNK) {
        const body = {
          orderIds: input.orderIds.slice(i, i + ORDER_STATUS_CHANGE_CHUNK),
          targetStatus: input.targetStatus,
          ...(input.courierCode ? { courierCode: input.courierCode } : {}),
          ...(input.trackingNo?.trim() ? { trackingNo: input.trackingNo.trim() } : {}),
        };
        const res = await adminFetch(PATH, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKeyFor('POST', PATH, { ...body, at: Date.now() }) },
          body: JSON.stringify(body),
        });
        // 앞 묶음이 이미 바뀐 뒤 실패하면 그 결과는 버리지 않고, 남은 주문을 실패로 돌려준다.
        if (!res.ok) {
          const err = await errorFromResponse(res);
          if (i === 0) throw err;
          const rest = input.orderIds.slice(i);
          merged.failCount += rest.length;
          merged.results.push(
            ...rest.map((orderId) => ({ orderId, orderNo: orderId, success: false, message: err.message, displayLabelCode: null })),
          );
          break;
        }
        const env = (await res.json()) as { data?: Partial<OrderStatusChangeResponse> | null };
        const d = env.data ?? {};
        merged.successCount += d.successCount ?? 0;
        merged.failCount += d.failCount ?? 0;
        merged.results.push(
          ...(d.results ?? []).map((r) => ({
            orderId: r.orderId,
            orderNo: r.orderNo,
            success: r.success === true,
            message: r.message ?? null,
            displayLabelCode: r.displayLabelCode ?? null,
          })),
        );
      }
      return merged;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['admin', 'order'] });
      await qc.invalidateQueries({ queryKey: ['admin', 'orders'] });
    },
  });
};
